/*
 * Copyright (C) 2025 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import React, {useRef, useEffect, forwardRef, useImperativeHandle} from 'react'
import {useScope as createI18nScope} from '@canvas/i18n'
import {Flex} from '@instructure/ui-flex'
import {Heading} from '@instructure/ui-heading'
import {CloseButton} from '@instructure/ui-buttons'
import {calculateMasqueradeHeight} from '@canvas/context-modules/differentiated-modules/utils/miscHelpers'
import CommentsTrayContentWithApollo from './CommentsTrayContentWithApollo'
import type {
  Submission,
  Assignment,
  ReviewerSubmission,
} from '@canvas/assignments/react/AssignmentsPeerReviewsStudentTypes'

const I18n = createI18nScope('peer_reviews_student')

export interface CommentsPanelHandle {
  focusCloseButton: () => void
}

interface CommentsPanelProps {
  submission: Submission
  assignment: Assignment
  reviewerSubmission?: ReviewerSubmission | null
  isMobile: boolean
  isOpen: boolean
  onClose: () => void
  onSuccessfulPeerReview: () => void
  isReadOnly?: boolean
  suppressSuccessAlert?: boolean
  autoFocusCloseButton?: boolean
  focusCommentInputTrigger?: number
}

export const CommentsPanel = forwardRef<CommentsPanelHandle, CommentsPanelProps>(
  (
    {
      submission,
      assignment,
      reviewerSubmission,
      isMobile,
      isOpen,
      onClose,
      onSuccessfulPeerReview,
      isReadOnly = false,
      suppressSuccessAlert = false,
      autoFocusCloseButton = false,
      focusCommentInputTrigger = 0,
    },
    ref,
  ) => {
    const closeButtonRef = useRef<HTMLButtonElement | null>(null)

    useImperativeHandle(
      ref,
      () => ({
        focusCloseButton: () => closeButtonRef.current?.focus(),
      }),
      [],
    )

    useEffect(() => {
      if (autoFocusCloseButton) {
        closeButtonRef.current?.focus()
      }
    }, [autoFocusCloseButton])

    return (
      <Flex.Item
        as="div"
        direction="column"
        size="327px"
        height="100%"
        padding="small"
        overflowY="auto"
        id="comments-panel"
        elementRef={(el: Element | null) => {
          if (el instanceof HTMLElement) {
            el.style.scrollPaddingBottom = `${calculateMasqueradeHeight() + 65}px`
          }
        }}
      >
        <Flex as="div" direction="column">
          <Flex.Item>
            <Flex as="div" direction="row" justifyItems="space-between">
              <Flex.Item>
                <Heading variant="titleModule" level="h2">
                  {I18n.t('Peer Comments')}
                </Heading>
              </Flex.Item>
              <Flex.Item padding="xx-small">
                <CloseButton
                  elementRef={(el: Element | null) => {
                    closeButtonRef.current = el as HTMLButtonElement
                  }}
                  screenReaderLabel={I18n.t('Close Peer Comments')}
                  size="small"
                  onClick={onClose}
                  data-testid="close-comments-button"
                />
              </Flex.Item>
            </Flex>
          </Flex.Item>
          <Flex.Item>
            <CommentsTrayContentWithApollo
              submission={submission}
              assignment={assignment}
              isPeerReviewEnabled={true}
              reviewerSubmission={reviewerSubmission}
              renderTray={isMobile}
              closeTray={onClose}
              open={isOpen}
              onSuccessfulPeerReview={onSuccessfulPeerReview}
              usePeerReviewModal={false}
              isReadOnly={isReadOnly}
              suppressSuccessAlert={suppressSuccessAlert}
              focusTrigger={focusCommentInputTrigger}
            />
          </Flex.Item>
        </Flex>
      </Flex.Item>
    )
  },
)

CommentsPanel.displayName = 'CommentsPanel'
